import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications } from "@/context/NotificationsContext";
import { fmtDateTime } from "@/lib/format";
import { PushPrompt } from "@/components/pwa/InstallButton";
import { cn } from "@/lib/utils";
import { getCompanyId, setCompanyId } from "@/lib/api";

/** Multi-company MDs: switch to the notification's company before opening it. */
export const openNotification = (n, navigate) => {
  if (n.company_id && getCompanyId() && n.company_id !== getCompanyId()) { setCompanyId(n.company_id); window.location.assign(n.url); return; }
  navigate(n.url);
};

export const NotificationBell = () => {
  const { items, unread, markRead, markAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const [ring, setRing] = useState(0);
  const prev = useRef(unread);
  useEffect(() => { if (unread > prev.current) setRing((r) => r + 1); prev.current = unread; }, [unread]);
  const navigate = useNavigate();
  const go = (n) => { markRead(n); setOpen(false); openNotification(n, navigate); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="hdr-btn hdr-bell relative inline-flex" aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} data-testid="notification-bell">
          <Bell key={ring} className={`h-6 w-6 lg:h-[22px] lg:w-[22px] ${ring ? "bell-wiggle" : ""}`} strokeWidth={2.4} />
          {unread > 0 && <span className="absolute -right-1 -top-1 min-w-[20px] rounded-full bg-gradient-to-r from-rose-500 to-pink-500 px-1.5 text-center text-[11px] font-bold leading-5 text-white shadow-md ring-2 ring-white" data-testid="notification-unread-count">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-1rem))] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="text-sm font-semibold text-slate-900">Notifications</span>
          {unread > 0 && <button type="button" className="text-xs font-medium text-teal-700 hover:underline" onClick={markAll} data-testid="notification-mark-all">Mark all read</button>}
        </div>
        <PushPrompt className="border-b border-slate-100" />
        <div className="max-h-[min(24rem,60dvh)] overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">No notifications yet</p>}
          {items.map((n) => (
            <button key={n.id} type="button" onClick={() => go(n)} className={cn("block w-full border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50", !n.read && "bg-teal-50/40")} data-testid={`notification-item-${n.id}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal-600" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{n.title}</div>
                  <div className="line-clamp-2 text-xs text-slate-600">{n.text}</div>
                  <div className="mt-1 text-[11px] text-slate-400">{fmtDateTime(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
